import { createContext, useContext, useState, type ReactNode } from "react";
import type { ContractType, ContractCompany } from "../services/contract-types";
import {
  readStorageItem,
  removeStorageItem,
  writeStorageItem,
} from "../utils/safeStorage";

export interface AddressDetails {
  id?: number;
  street: string;
  house_number: string;
  zip_code: string;
  city: string;
  country: string;
  latitude: number;
  longitude: number;
}

export interface UtilityContractDetails {
  previous_provider_name: string;
  previous_customer_number: string;
  annual_consumption_kwh: string;
  meter_point_id: string;
  meter_number: string;
  meter_reading: string;
  unit_price_ct_kwh: string;
  base_price_eur_month: string;
  delivery_start_date: string;
  as_soon_as_possible: boolean;
  desired_monthly_payment: string;
}

export const createEmptyUtilityContractDetails =
  (): UtilityContractDetails => ({
    previous_provider_name: "",
    previous_customer_number: "",
    annual_consumption_kwh: "",
    meter_point_id: "",
    meter_number: "",
    meter_reading: "",
    unit_price_ct_kwh: "",
    base_price_eur_month: "",
    delivery_start_date: "",
    as_soon_as_possible: false,
    desired_monthly_payment: "",
  });

interface UtilityDetailsGroup {
  electricity: UtilityContractDetails;
  gas: UtilityContractDetails;
}

export interface ContractData {
  address: AddressDetails | null;
  name: string;
  floor: string;
  customerState: string;
  comments: string;
  gender: number | null; // 1=Herr (male), 2=Frau (female), 3=Divers (other)
  contract: {
    firstName: string;
    birthDate: string;
    customerNumber: string;
    email: string;
    phone: string;
    category: string;
    company: string;
    bank_details: string;
    signature_path: string;
  };
  utilityDetails: UtilityDetailsGroup;
  selectedContractTypes: (ContractType & {
    company_details: ContractCompany;
  })[];
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
type SetTempAddress = (address: AddressDetails | null) => void;

type UpdateContractDataPayload = Partial<
  Omit<ContractData, "contract" | "utilityDetails">
> & {
  contract?: Partial<ContractData["contract"]>;
  utilityDetails?: {
    electricity?: Partial<UtilityContractDetails>;
    gas?: Partial<UtilityContractDetails>;
  };
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
type UpdateContractData = (data: UpdateContractDataPayload) => void;
// eslint-disable-next-line @typescript-eslint/no-unused-vars
type AddContractType = (
  contractType: ContractType & { company_details: ContractCompany }
) => void;
// eslint-disable-next-line @typescript-eslint/no-unused-vars
type RemoveContractType = (contractTypeId: number) => void;

interface ContractContextType {
  tempAddress: AddressDetails | null;
  setTempAddress: SetTempAddress;
  contractData: ContractData;
  updateContractData: UpdateContractData;
  addContractType: AddContractType;
  removeContractType: RemoveContractType;
}

const ContractContext = createContext<ContractContextType | undefined>(
  undefined
);

const CONTRACT_DATA_STORAGE_KEY = "contractData";
const CONTRACT_DATA_STORAGE_VERSION = 1;

const createEmptyContract = () => ({
  firstName: "",
  birthDate: "",
  customerNumber: "",
  email: "",
  phone: "",
  category: "",
  company: "",
  bank_details: "",
  signature_path: "",
});

type PersistedContractData = {
  storageVersion?: number;
  address?: AddressDetails | null;
  name?: string;
  floor?: string;
  customerState?: string;
  comments?: string;
  gender?: number | null;
  contract?: Partial<ContractData["contract"]>;
  utilityDetails?: {
    electricity?: Partial<UtilityContractDetails>;
    gas?: Partial<UtilityContractDetails>;
  };
  selectedContractTypes?: (ContractType & {
    company_details: ContractCompany;
  })[];
};

const isValidContractCompany = (value: unknown): value is ContractCompany => {
  if (!value || typeof value !== "object") return false;
  const record = value as Partial<ContractCompany>;
  return (
    typeof record.company === "string" &&
    record.company.trim() !== "" &&
    typeof record.color_code === "string" &&
    record.color_code.trim() !== ""
  );
};

const isValidSelectedContractType = (
  value: unknown
): value is ContractType & { company_details: ContractCompany } => {
  if (!value || typeof value !== "object") return false;
  const record = value as Partial<ContractType> & {
    company_details?: unknown;
  };

  return (
    typeof record.id === "number" &&
    typeof record.type === "string" &&
    record.type.trim() !== "" &&
    typeof record.category === "string" &&
    record.category.trim() !== "" &&
    isValidContractCompany(record.company_details)
  );
};

const normalizeSelectedContractTypes = (
  value: unknown
): (ContractType & { company_details: ContractCompany })[] => {
  if (!Array.isArray(value)) return [];
  return value.filter(isValidSelectedContractType);
};

const normalizeUtilityContractDetails = (
  value: unknown
): UtilityContractDetails => {
  const defaults = createEmptyUtilityContractDetails();
  if (!value || typeof value !== "object") {
    return defaults;
  }

  const record = value as Partial<UtilityContractDetails>;
  return {
    previous_provider_name:
      typeof record.previous_provider_name === "string"
        ? record.previous_provider_name
        : defaults.previous_provider_name,
    previous_customer_number:
      typeof record.previous_customer_number === "string"
        ? record.previous_customer_number
        : defaults.previous_customer_number,
    annual_consumption_kwh:
      typeof record.annual_consumption_kwh === "string"
        ? record.annual_consumption_kwh
        : defaults.annual_consumption_kwh,
    meter_point_id:
      typeof record.meter_point_id === "string"
        ? record.meter_point_id
        : defaults.meter_point_id,
    meter_number:
      typeof record.meter_number === "string"
        ? record.meter_number
        : defaults.meter_number,
    meter_reading:
      typeof record.meter_reading === "string"
        ? record.meter_reading
        : defaults.meter_reading,
    unit_price_ct_kwh:
      typeof record.unit_price_ct_kwh === "string"
        ? record.unit_price_ct_kwh
        : defaults.unit_price_ct_kwh,
    base_price_eur_month:
      typeof record.base_price_eur_month === "string"
        ? record.base_price_eur_month
        : defaults.base_price_eur_month,
    delivery_start_date:
      typeof record.delivery_start_date === "string"
        ? record.delivery_start_date
        : defaults.delivery_start_date,
    as_soon_as_possible:
      typeof record.as_soon_as_possible === "boolean"
        ? record.as_soon_as_possible
        : defaults.as_soon_as_possible,
    desired_monthly_payment:
      typeof record.desired_monthly_payment === "string"
        ? record.desired_monthly_payment
        : defaults.desired_monthly_payment,
  };
};

const normalizeUtilityDetailsGroup = (value: unknown): UtilityDetailsGroup => {
  const defaults = {
    electricity: createEmptyUtilityContractDetails(),
    gas: createEmptyUtilityContractDetails(),
  };

  if (!value || typeof value !== "object") {
    return defaults;
  }

  const record = value as PersistedContractData["utilityDetails"];
  return {
    electricity: normalizeUtilityContractDetails(record?.electricity),
    gas: normalizeUtilityContractDetails(record?.gas),
  };
};

export function ContractProvider({ children }: { children: ReactNode }) {
  const [contractData, setContractData] = useState<ContractData>(() => {
    const saved = readStorageItem(CONTRACT_DATA_STORAGE_KEY);
    let parsedData: PersistedContractData = {};
    if (saved) {
      try {
        const candidate = JSON.parse(saved);
        if (candidate && typeof candidate === "object") {
          parsedData = candidate as PersistedContractData;
        }
      } catch (error) {
        console.warn("Could not parse cached contractData, using defaults", {
          error,
        });
        removeStorageItem(CONTRACT_DATA_STORAGE_KEY);
      }
    }

    const parsedContract = parsedData.contract ?? {};

    return {
      address: parsedData.address ?? null,
      name: typeof parsedData.name === "string" ? parsedData.name : "",
      floor: typeof parsedData.floor === "string" ? parsedData.floor : "",
      customerState:
        typeof parsedData.customerState === "string"
          ? parsedData.customerState
          : "",
      comments:
        typeof parsedData.comments === "string" ? parsedData.comments : "",
      gender: typeof parsedData.gender === "number" ? parsedData.gender : null,
      contract: {
        ...createEmptyContract(),
        ...parsedContract,
      },
      utilityDetails: normalizeUtilityDetailsGroup(parsedData.utilityDetails),
      selectedContractTypes: normalizeSelectedContractTypes(
        parsedData.selectedContractTypes
      ),
    };
  });

  const updateContractData = (data: UpdateContractDataPayload) => {
    setContractData((previousContractData) => {
      const newData = {
        ...previousContractData,
        ...data,
        selectedContractTypes: data.selectedContractTypes
          ? normalizeSelectedContractTypes(data.selectedContractTypes)
          : previousContractData.selectedContractTypes,
        contract: data.contract
          ? { ...previousContractData.contract, ...data.contract }
          : previousContractData.contract || createEmptyContract(),
        utilityDetails: data.utilityDetails
          ? {
              electricity: data.utilityDetails.electricity
                ? {
                    ...previousContractData.utilityDetails.electricity,
                    ...data.utilityDetails.electricity,
                  }
                : previousContractData.utilityDetails.electricity,
              gas: data.utilityDetails.gas
                ? {
                    ...previousContractData.utilityDetails.gas,
                    ...data.utilityDetails.gas,
                  }
                : previousContractData.utilityDetails.gas,
            }
          : previousContractData.utilityDetails,
      };
      writeStorageItem(
        CONTRACT_DATA_STORAGE_KEY,
        JSON.stringify({
          ...newData,
          storageVersion: CONTRACT_DATA_STORAGE_VERSION,
          selectedContractTypes: normalizeSelectedContractTypes(
            newData.selectedContractTypes
          ),
        })
      );
      return newData;
    });
  };

  const addContractType = (
    contractType: ContractType & { company_details: ContractCompany }
  ) => {
    // Check if contract type is already selected
    if (
      !contractData.selectedContractTypes.some(
        (ct) => ct.id === contractType.id
      )
    ) {
      const updatedContractTypes = [
        ...contractData.selectedContractTypes,
        contractType,
      ];
      updateContractData({ selectedContractTypes: updatedContractTypes });
    }
  };

  const removeContractType = (contractTypeId: number) => {
    const updatedContractTypes = contractData.selectedContractTypes.filter(
      (ct) => ct.id !== contractTypeId
    );
    updateContractData({ selectedContractTypes: updatedContractTypes });
  };

  return (
    <ContractContext.Provider
      value={{
        tempAddress: contractData.address,
        setTempAddress: (newAddress) =>
          updateContractData({ address: newAddress }),
        contractData,
        updateContractData,
        addContractType,
        removeContractType,
      }}
    >
      {children}
    </ContractContext.Provider>
  );
}

export function useContract() {
  const context = useContext(ContractContext);
  if (context === undefined) {
    throw new Error("useContract must be used within a ContractProvider");
  }
  return context;
}
